import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FileText, MapPin, User } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import ViewNav from "@/components/nav/ViewNav";
import api from "@/lib/api";
import { categoryLabel } from "@/lib/catalog";
import { from } from "@/lib/cameFrom";
import { locationText } from "@/lib/jobDisplay";
import { money } from "@/lib/money";
import { QUOTE_STATUSES, quoteStatusClass, quoteStatusLabel } from "@/lib/quoteStatus";
import { formatDate } from "@/lib/dates";

export default function ProviderQuotes() {
  const { t } = useTranslation();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [filter, setFilter] = useState("");
  const [reload, setReload] = useState(0);
  // N25. The quote being withdrawn right now, so the row can say so and the
  // button cannot be pressed twice — a second POST would 400 on a quote that
  // is no longer `sent`, and the reader would see an error for something that
  // worked.
  const [withdrawing, setWithdrawing] = useState("");
  // N45. The quote whose withdrawal has been asked for and not yet confirmed.
  // One at a time by construction: asking about a second row replaces the
  // first, which is the same thing pressing "Conservarla" would do.
  const [confirming, setConfirming] = useState("");
  const [withdrawFailed, setWithdrawFailed] = useState("");

  /**
   * DY2.2 V4. This was `.then(...)` with no `.catch` and no `finally`, so
   * `setLoading(false)` lived on the success path alone: any rejection — a 500,
   * an expired session, a dropped connection — left `loading` true forever and
   * the page animating three skeleton cards with no error, no empty state and
   * no way out but a reload. Reported from production on 2026-08-29, and the
   * third silent failure found that day.
   *
   * `finally` owns the flag now, so it turns off however the request ended, and
   * a failure is a state the page can render rather than one it sits in.
   */
  useEffect(() => {
    let live = true;
    setLoading(true);
    setFailed(false);
    api.get("/quotes?mine=true")
      .then((r) => { if (live) setQuotes(r.data.quotes || []); })
      .catch(() => { if (live) setFailed(true); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [reload]);

  const filtered = filter ? quotes.filter((q) => q.status === filter) : quotes;

  /**
   * N25. The endpoint has existed since the MVP and nothing in the app called
   * it, so the path Dany names out loud — "the way is to withdraw it from your
   * Quotes page" — led to a page with no such button. It does now.
   *
   * **N45: two taps in line, not `window.confirm`.** The native dialog was
   * chosen because withdrawing is reversible in the only sense that matters —
   * the job stays open and a new quote can be sent — and a bespoke modal would
   * be more ceremony than the act deserves. Both halves of that are still
   * true; what was wrong is that `window.confirm` is not the third option. It
   * is the one piece of this page that is not this page: it cannot be
   * translated by i18next, so a Spanish reader gets "OK / Cancel"; it takes
   * over the whole window on a phone, detached from the row it is asking
   * about; and it blocks the thread.
   *
   * So it uses the pattern the draft card already uses
   * (`DanyJobDraftWidget`, N9 e): the button becomes the commit, a "Conservarla"
   * appears beside it, and the sentence about what happens sits under both. No
   * new component — the same two pieces of state and the same three labels.
   */
  const ask = (event, quoteId) => {
    // The row is a Link. Without this the browser follows it and the reader
    // lands on the job page wondering whether the press registered.
    event.preventDefault();
    event.stopPropagation();
    setWithdrawFailed("");
    setConfirming((current) => (current === quoteId ? "" : quoteId));
  };

  const withdraw = async (event, quoteId) => {
    event.preventDefault();
    event.stopPropagation();
    setConfirming("");
    setWithdrawFailed("");
    setWithdrawing(quoteId);
    try {
      await api.post(`/quotes/${quoteId}/withdraw`);
      setReload((n) => n + 1);
    } catch {
      // In the page rather than in an alert, for the reason the confirmation
      // moved: a reader who cannot read the message cannot act on it.
      setWithdrawFailed(quoteId);
    } finally {
      setWithdrawing("");
    }
  };

  // Built from the same list the badge reads its word from, so the two cannot
  // drift apart. The tabs keep the plural filter wording — "Enviadas" labels a
  // set of quotes, "Enviada" labels one — but there is a single set of statuses.
  const filters = [
    { v: "", label: t("providerQuotesX.filters.all") },
    ...QUOTE_STATUSES.map((v) => ({ v, label: t(`providerQuotesX.filters.${v}`) })),
  ];

  return (
    <DashboardLayout role="provider">
      <div data-testid="provider-quotes-page">
        <h1 className="font-display text-3xl md:text-4xl font-bold text-worker">{t("providerQuotesX.title")}</h1>

        {/* One scrolling line below 992px (T13-M3). The `.chip` further down
            this page still labels a category on each card, and is untouched:
            that one is not a control. */}
        <ViewNav
          className="mt-6 mb-4"
          items={filters.map((f) => ({
            key: f.v, label: f.label, testId: `quote-filter-${f.v || "all"}`,
          }))}
          active={filter}
          onSelect={setFilter}
        />

        {loading ? (
          <div className="space-y-3">{Array(3).fill(0).map((_, i) => <div key={i} className="skeleton h-24" />)}</div>
        ) : failed ? (
          <div className="card-base p-10 text-center" data-testid="provider-quotes-error">
            <FileText size={32} className="mx-auto text-smoke" />
            <p className="mt-3 text-smoke">{t("common.loadFailed")}</p>
            <button className="fx-btn fx-btn--primary mt-4" onClick={() => setReload((n) => n + 1)}
                    data-testid="provider-quotes-retry">{t("common.retry")}</button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="card-base p-10 text-center">
            <FileText size={32} className="mx-auto text-smoke" />
            <p className="mt-3 text-smoke">{t("providerQuotesX.empty")}</p>
            <Link to="/provider/browse" className="fx-btn fx-btn--primary mt-4">{t("providerQuotesX.browseCta")}</Link>
          </div>
        ) : (
          <div className="space-y-3" data-testid="provider-quotes-list">
            {filtered.map((q) => {
              const j = q.job_snippet || {};
              const where = locationText(j.city, j.state);
              // T19. A deleted post has no page to open — it answers 404 — so
              // its row is plain text; every other row opens the job.
              const gone = j.closed_by === "deleted";
              const Shell = gone ? "div" : Link;
              const shellProps = gone ? {} : { to: `/provider/jobs/${q.job_id}`, state: from("/provider/quotes") };
              return (
                <Shell key={q.quote_id} {...shellProps}
                      className="card-base p-5 flex justify-between items-start gap-3 flex-wrap" data-testid={`q-${q.quote_id}`}>
                  <div className="min-w-0">
                    {/* The job's own title leads. The hash stays — it is what
                        Jorge and support quote at each other — but as metadata
                        under the line, not as the line. */}
                    <p className="font-display font-bold text-worker truncate" data-testid={`q-title-${q.quote_id}`}>
                      {j.title || t("providerQuotesX.jobLabel", { id: q.job_id.slice(-6) })}
                    </p>
                    {j.requester?.full_name && (
                      <p className="text-sm text-smoke mt-1 flex items-center gap-1 truncate" data-testid={`q-requester-${q.quote_id}`}>
                        <User size={13} className="shrink-0" />{j.requester.full_name}
                      </p>
                    )}
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      {j.category && <span className="chip">{categoryLabel(t, j.category)}</span>}
                      {where && (
                        <span className="text-xs text-smoke flex items-center gap-1"><MapPin size={12} />{where}</span>
                      )}
                    </div>
                    {/* T19. Which act closed the job: "Rechazada" alone reads
                        as the requester turning the quote down. The copy is the
                        bell's own — `job_deleted`, and the chat's line for a
                        cancellation — with the reason when it is theirs. */}
                    {j.closed_by && (
                      <p className="text-xs text-smoke mt-2" data-testid={`q-closed-${q.quote_id}`}>
                        {j.closed_by === "deleted"
                          ? t("providerQuotesX.closedDeleted")
                          : t("chatX.sysJobCanceled", { client: j.requester?.full_name || "" })}
                        {j.cancel_reason && ` ${t("adminX.jobs.reasonLabel")} ${j.cancel_reason}`}
                      </p>
                    )}
                    <p className="text-xs text-smoke font-mono mt-2">
                      {formatDate(q.created_at)} · #{q.job_id.slice(-6)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-xl font-bold text-worker">{money(q.total_due, "$0.00")}</p>
                    <span className={`badge mt-1 ${quoteStatusClass(q.status)}`} data-testid={`q-status-${q.quote_id}`}>
                      {quoteStatusLabel(t, q.status)}
                    </span>
                    {/* Only on `sent` — and `stale` (T19), an outdated quote
                        that is still theirs. An accepted quote is a commitment
                        and a declined or withdrawn one has nothing left to
                        withdraw; the server refuses all three, and a button
                        that exists only to be refused is a worse answer than
                        no button. */}
                    {(q.status === "sent" || q.status === "stale") && (
                      <div className="mt-2 flex flex-col items-end gap-1">
                        <div className="flex items-center gap-2 flex-wrap justify-end">
                          <button
                            type="button"
                            className={confirming === q.quote_id
                              ? "fx-btn fx-btn--ghost text-xs bg-danger text-cream hover:bg-danger"
                              : "fx-btn fx-btn--ghost text-xs"}
                            onClick={(e) => (confirming === q.quote_id
                              ? withdraw(e, q.quote_id)
                              : ask(e, q.quote_id))}
                            disabled={withdrawing === q.quote_id}
                            data-testid={`q-withdraw-${q.quote_id}`}
                          >
                            {withdrawing === q.quote_id
                              ? t("providerQuotesX.withdrawing")
                              : confirming === q.quote_id
                                ? t("providerQuotesX.withdrawConfirmAction")
                                : t("providerQuotesX.withdraw")}
                          </button>
                          {confirming === q.quote_id && (
                            <button
                              type="button"
                              className="fx-btn fx-btn--ghost text-xs"
                              onClick={(e) => ask(e, q.quote_id)}
                              data-testid={`q-withdraw-cancel-${q.quote_id}`}
                            >
                              {t("providerQuotesX.withdrawKeep")}
                            </button>
                          )}
                        </div>
                        {confirming === q.quote_id && (
                          <p className="text-[11px] text-smoke text-right max-w-[22rem]"
                             data-testid={`q-withdraw-hint-${q.quote_id}`}>
                            {t("providerQuotesX.withdrawConfirm")}
                          </p>
                        )}
                        {withdrawFailed === q.quote_id && (
                          <p className="text-[11px] text-danger text-right"
                             data-testid={`q-withdraw-error-${q.quote_id}`}>
                            {t("providerQuotesX.withdrawFailed")}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </Shell>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
